import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useEffect, useRef, useState } from 'react';
import { makeApiProvider } from '@pflegenavi/shared-frontend/platform';
import type {
  ISettlementPhoenixApi,
  RecurringItemsChargeParams,
  ExitResidentParams,
  ArchiveResidentParams,
  DeceasedFundsPayoutProcessDto,
} from './api';
import { SettlementPhoenixApi } from './api';
import type { ShowResidentDto } from '@pflegenavi/shared/api';
import {
  invalidateResident,
  RESIDENT_SETTLEMENT_KEY,
} from '../resident/queryKeys';
import {
  getTransactionsKey,
  RECEIPT_BATCH_LIST_PAGINATED_KEY,
  TRANSACTIONS_PAGINATED_KEY,
} from '../transaction/queryKeys';
import { invalidateCashList } from '../cash-management/queryKeys';
import { CASH_LISTS_PHOENIX_KEY } from '../cash-management-phoenix/queryKeys';
import { invalidateAllResidentBalance } from '../reporting/queryKeys';
import { SHOW_RESIDENT_KEY } from '../resident-phoenix/queryKeys';
import { TRANSACTIONS_PHOENIX_KEY } from '../transaction-phoenix/queryKeys';
import { RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY } from '../receipt-phoenix/queryKeys';

// TODO: These constants are duplicated from pages/residents/src/lib/account/exiting/types.ts
// They should be moved to a shared location to avoid circular dependencies
const TERMINAL_STATUSES: readonly string[] = [
  'withdrawal_recorded',
  'payout_enqueued',
  'payout_created',
  'payout_completed',
  'payout_refund_pending',
  'payout_refund_partial',
  'payout_refunded_full',
  'failed',
];

const WITHDRAWAL_AND_BEYOND_STATUSES: readonly string[] = [
  'withdrawal_recorded',
  'payout_enqueued',
  'payout_created',
  'payout_completed',
  'payout_refund_pending',
  'payout_refund_partial',
  'payout_refunded_full',
];

const sortByUpdatedAtDesc = <
  T extends { updated_at?: string; inserted_at?: string }
>(
  a: T,
  b: T
): number => {
  const aTime = Date.parse(a.updated_at ?? a.inserted_at ?? '');
  const bTime = Date.parse(b.updated_at ?? b.inserted_at ?? '');
  return isNaN(bTime - aTime) ? 0 : bTime - aTime;
};

const ApiContext = createContext<ISettlementPhoenixApi | undefined>(undefined);
const {
  useApi: useSettlementPhoenixApi,
  ApiProvider: SettlementPhoenixApiProvider,
} = makeApiProvider({
  name: 'RecurringItemPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new SettlementPhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useSettlementPhoenixApi,
  SettlementPhoenixApiProvider,
  ApiContext as SettlementPhoenixApiContext,
};

export const useChargeRecurringItemPhoenix = (
  nursingHomeId?: string
): UseMutationResult<void, unknown, RecurringItemsChargeParams> => {
  const api = useSettlementPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<void, unknown, RecurringItemsChargeParams>({
    mutationKey: ['CHARGE_RECURRING_ITEM'],
    mutationFn: (data) => api.recurringItemsCharge(data),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RESIDENT_SETTLEMENT_KEY(variables.params.residentId),
        }),
        invalidateResident(
          queryClient,
          variables.params.residentId,
          nursingHomeId
        ),
        queryClient.invalidateQueries({
          queryKey: getTransactionsKey(variables.params.residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({ nursingHomeId }),
        }),
      ]);
    },
  });
  return result;
};

export const useExitResidentPhoenix = (
  nursingHomeId?: string
): UseMutationResult<ShowResidentDto, unknown, ExitResidentParams> => {
  const api = useSettlementPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<ShowResidentDto, unknown, ExitResidentParams>({
    mutationKey: ['EXIT_RESIDENT'],
    mutationFn: (data) => api.exitResident(data),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RESIDENT_SETTLEMENT_KEY(variables.params.residentId),
        }),
        invalidateResident(
          queryClient,
          variables.params.residentId,
          nursingHomeId
        ),
        queryClient.invalidateQueries({
          queryKey: getTransactionsKey(variables.params.residentId),
        }),
      ]);
    },
  });
  return result;
};

export interface UseDeceasedFundsPayoutStatusOptions {
  residentId: string;
  enabled?: boolean;
  pollingInterval?: number;
  onWithdrawalRecorded?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
}

export interface UseDeceasedFundsPayoutStatusReturn {
  currentProcess: DeceasedFundsPayoutProcessDto | null;
  status: string | null;
  isFetching: boolean;
  isLoading: boolean; // Initial loading state
  error: Error | null;
  hasReachedWithdrawal: boolean;
  isNonTerminalProcess: boolean; // Whether there's an active process
  refetch: () => void;
}

export const useDeceasedFundsPayoutStatus = ({
  residentId,
  enabled = false,
  pollingInterval = 2000,
  onWithdrawalRecorded,
  onError,
  timeout = 120000,
}: UseDeceasedFundsPayoutStatusOptions): UseDeceasedFundsPayoutStatusReturn => {
  const api = useSettlementPhoenixApi();
  const [enabledState, setEnabledState] = useState(enabled);
  const hasCalledWithdrawalRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Set up timeout when enabled
  useEffect(() => {
    if (enabled && timeout) {
      timeoutRef.current = setTimeout(() => {
        setEnabledState(false);
      }, timeout);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
    return undefined;
  }, [enabled, timeout]);

  const queryResult = useQuery({
    queryKey: ['deceased-funds-payout-processes', residentId],
    queryFn: async () => {
      const response = await api.getDeceasedFundsPayoutProcesses({
        params: { residentId },
      });
      const processes = response.data;
      const latestProcess = [...processes].sort(sortByUpdatedAtDesc)[0] ?? null;
      return latestProcess;
    },
    enabled: enabled && enabledState,
    refetchInterval: (query) => {
      const latest = query.state.data as DeceasedFundsPayoutProcessDto | null;
      const terminal =
        latest?.status && TERMINAL_STATUSES.includes(latest.status);
      return !terminal ? pollingInterval : false;
    },
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    retry: 0,
  });

  const {
    data,
    error: queryError,
    refetch,
    isFetching,
    isLoading,
  } = queryResult;
  const currentProcess = data ?? null;
  const status = currentProcess?.status ?? null;
  const hasReachedWithdrawal = status
    ? WITHDRAWAL_AND_BEYOND_STATUSES.includes(status)
    : false;
  const isNonTerminalProcess = status
    ? !TERMINAL_STATUSES.includes(status)
    : false;

  // Call onWithdrawalRecorded callback once when withdrawal is reached
  useEffect(() => {
    if (hasReachedWithdrawal && !hasCalledWithdrawalRef.current) {
      hasCalledWithdrawalRef.current = true;
      onWithdrawalRecorded?.();
    }
  }, [hasReachedWithdrawal, onWithdrawalRecorded]);

  // Call onError callback when error occurs
  useEffect(() => {
    if (queryError && onError) {
      onError(queryError);
    }
  }, [queryError, onError]);

  // Reset the callback ref when enabled changes
  useEffect(() => {
    if (enabled) {
      hasCalledWithdrawalRef.current = false;
      setEnabledState(true);
    } else {
      setEnabledState(false);
    }
  }, [enabled]);

  return {
    currentProcess,
    status,
    isFetching,
    isLoading,
    error: queryError instanceof Error ? queryError : null,
    hasReachedWithdrawal,
    isNonTerminalProcess,
    refetch,
  };
};

export const useArchiveResidentPhoenix = (
  residentId: string,
  nursingHomeId?: string
): UseMutationResult<
  ShowResidentDto,
  unknown,
  ArchiveResidentParams['body']
> => {
  const api = useSettlementPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<ShowResidentDto, unknown, ArchiveResidentParams['body']>({
    mutationKey: ['ARCHIVE_RESIDENT_PHOENIX'],
    mutationFn: (body) =>
      api.archiveResident({
        params: { residentId },
        body,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RESIDENT_SETTLEMENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: getTransactionsKey(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PAGINATED_KEY({
            nursingHomeId: nursingHomeId ?? '',
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: SHOW_RESIDENT_KEY(residentId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_PHOENIX_KEY(nursingHomeId),
        }),
        invalidateAllResidentBalance(queryClient, nursingHomeId),
        invalidateCashList(queryClient, nursingHomeId),
        invalidateResident(queryClient, residentId, nursingHomeId),
      ]);
    },
  });
};
